import { node } from "~/util/dom";
import { getEmo1PosSvg, getEmo2PosSvg, getEmo1NegSvg, getEmo2NegSvg, getRandomId, SVG_SIZE, getEmoNeuNegSvg, getEmoNeuPosSvg } from "./emojis-svgs";

export interface IEmojiOutput {
	container: HTMLButtonElement;
	setActive: (state?: boolean) => void;
}

interface IEmojiProps {
	active?: boolean;
	onClick?: () => void;
	hover?: boolean;
	size?: number;
}

interface ICreateEmoji extends Omit<IEmojiProps, "size"> {
	className: string;
	svg: string;
	actFill?: string;
	defFill?: string;
}

function createEmoji({
	active = false,
	onClick = null,
	hover = true,
	className = "",
	svg = "",
	actFill = "#fff",
	defFill = "#f0f0f0",
}: ICreateEmoji): IEmojiOutput {
	let activeState = active;
	const button = node("button", { className });

	button.setAttribute("data-log-ignore", "1");
	button.innerHTML = svg;

	function setActive(state?: boolean): void {
		const path = button.querySelector("svg path") as SVGPathElement;

		path.style.fill = state ? actFill : defFill;
	}

	if (hover) {
		button.addEventListener("pointerenter", () => {
			setActive(true);
		});
		button.addEventListener("pointerleave", () => {
			if (!activeState) {
				setActive();
			}
		});
		onClick && button.addEventListener("click", () => {
			onClick();
		});
	} else {
		button.style.cursor = "default";
	}

	setActive(activeState);

	return {
		container: button,
		setActive: (state?: boolean) => {
			activeState = state;

			setActive(activeState);
		},
	};
}

export function getEmo1Pos({
	active = false,
	onClick = null,
	hover = true,
	size = SVG_SIZE,
}: IEmojiProps): IEmojiOutput {
	const gradientId = getRandomId();
	const svg = getEmo1PosSvg({
		gradientId,
		size,
	});

	return createEmoji({
		active,
		onClick,
		hover,
		className: "emoji-btn emo1-pos",
		actFill: `url(#${gradientId})`,
		svg,
	});
}

export function getEmo2Pos({
	active = false,
	onClick = null,
	hover = true,
	size = SVG_SIZE,
}: IEmojiProps): IEmojiOutput {
	return createEmoji({
		active,
		onClick,
		hover,
		className: "emoji-btn emo2-pos",
		actFill: "#8FDD7F",
		svg: getEmo2PosSvg({
			size,
		}),
	});
}

export function getEmo1Neg({
	active = false,
	onClick = null,
	hover = true,
	size = SVG_SIZE,
}: IEmojiProps): IEmojiOutput {
	const gradientId = getRandomId();
	const svg = getEmo1NegSvg({
		gradientId,
		size,
	});

	return createEmoji({
		active,
		onClick,
		hover,
		className: "emoji-btn emo1-neg",
		actFill: `url(#${gradientId})`,
		svg,
	});
}

export function getEmo2Neg({
	active = false,
	onClick = null,
	hover = true,
	size = SVG_SIZE,
}: IEmojiProps): IEmojiOutput {
	return createEmoji({
		active,
		onClick,
		hover,
		className: "emoji-btn emo2-neg",
		actFill: "#FF9663",
		svg: getEmo2NegSvg({
			size,
		}),
	});
}

export function getEmoNeu({
	active = false,
	hover = true,
	size = SVG_SIZE,
	onClick = null,
}: IEmojiProps): IEmojiOutput {
	const button = node("button", { className: "emoji-btn emo-neu" });
	const posSvg = getEmoNeuPosSvg({
		size,
	});
	const negSvg = getEmoNeuNegSvg({
		size,
	});
	let activeState = active;

	button.setAttribute("data-log-ignore", "1");
	button.innerHTML = `${posSvg}${negSvg}`;
	button.style.cursor = "pointer";

	function setActive(state?: boolean) {
		if (state) {
			button.classList.add("btn-active");
		} else {
			button.classList.remove("btn-active");
		}
	}

	if (!active && hover) {
		button.addEventListener("pointerenter", () => {
			setActive(true);
		});
		button.addEventListener("pointerleave", () => {
			if (!activeState) {
				setActive();
			}
		});
	}

	onClick && button.addEventListener("click", () => {
		onClick();
	});

	return {
		container: button,
		setActive: (state?: boolean) => {
			activeState = state;

			setActive(activeState);
		},
	};
}
